/**
 * Rule-based chord-progression generator (FEATURES F-LEARN-04).
 *
 * Transparent by design: a weight table of diatonic chord moves, adjusted by
 * style and mood multipliers, sampled with a seeded RNG. Every generated move
 * is explained from the actual chords (root motion, shared notes, function),
 * never from free text.
 */
import { seededRng, weightedPick, type Rng } from "../core/random";
import type { ChordQuality } from "./chords";
import { diatonicChord, DEGREES, type Degree, type DiatonicChord } from "./diatonic";
import type { MusicalKey } from "./key";
import { intervalName } from "./notes";

export type Style = "pop" | "worship" | "jazz" | "classical";
export type Mood = "neutral" | "bright" | "melancholic" | "tense";
export type Cadence = "authentic" | "plagal" | "half" | "none";

export interface GeneratorOptions {
  key: MusicalKey;
  length: number; // 2–8 bars, one chord per bar
  style: Style;
  mood: Mood;
  complexity: 1 | 2 | 3; // 1 triads, 2 sevenths on ii/V, 3 sevenths throughout
  startOnTonic: boolean;
  cadence: Cadence;
  seed: number;
}

export interface GeneratedChord {
  degree: Degree;
  seventh: boolean;
  chord: DiatonicChord;
  /** Why this chord follows the previous one (grounded in the notes). Empty for the first chord. */
  reason: string;
}

export interface GeneratedProgression {
  chords: GeneratedChord[];
  cadenceText: string | null;
  seed: number;
}

/** Base likelihood of moving from one degree (row) to another (column), 1-indexed; common-practice tendencies. */
export const BASE_WEIGHTS: Record<Degree, Partial<Record<Degree, number>>> = {
  1: { 4: 3, 5: 3, 6: 3, 2: 2, 3: 1 },
  2: { 5: 5, 4: 1, 7: 1 },
  3: { 6: 4, 4: 2 },
  4: { 5: 4, 1: 3, 2: 2, 6: 1 },
  5: { 1: 5, 6: 3, 4: 1 },
  6: { 4: 4, 2: 3, 5: 2, 3: 1 },
  7: { 1: 5, 3: 1 },
};

/** Style multipliers on specific moves ("from>to"). */
export const STYLE_BIAS: Record<Style, Record<string, number>> = {
  pop: { "1>5": 2, "5>6": 2, "6>4": 2.5, "4>1": 1.5 },
  worship: { "4>1": 2.5, "1>5": 2, "6>4": 2, "5>6": 1.5, "4>5": 1.5 },
  jazz: { "2>5": 3, "5>1": 2, "6>2": 2.5, "1>6": 2 },
  classical: { "2>5": 2, "4>5": 2, "5>1": 2, "1>4": 1.5 },
};

type QualityFamily = "major" | "minor" | "diminished";
const FAMILY: Partial<Record<ChordQuality, QualityFamily>> = {
  maj: "major", maj7: "major", "7": "major", min: "minor", m7: "minor", dim: "diminished", m7b5: "diminished",
};

/** Mood multipliers by the target chord's quality family. */
export const MOOD_BIAS: Record<Mood, Record<QualityFamily, number>> = {
  neutral: { major: 1, minor: 1, diminished: 1 },
  bright: { major: 2, minor: 0.7, diminished: 0.5 },
  melancholic: { major: 0.7, minor: 2.2, diminished: 1 },
  tense: { major: 1, minor: 1, diminished: 3 },
};

const CADENCE_DEGREES: Record<Cadence, Degree[]> = { authentic: [5, 1], plagal: [4, 1], half: [5], none: [] };

function weightsFrom(opts: GeneratorOptions, from: Degree, next?: Degree): number[] {
  return DEGREES.map((to) => {
    if (to === from) return 0;
    let w = BASE_WEIGHTS[from][to] ?? 0.2; // rare moves stay possible
    w *= STYLE_BIAS[opts.style][`${from}>${to}`] ?? 1;
    const fam = FAMILY[diatonicChord(opts.key, to).quality] ?? "major";
    w *= MOOD_BIAS[opts.mood][fam];
    // Look ahead to a fixed next chord (e.g. the cadence) so the approach is natural.
    if (next !== undefined) w *= to === next ? 0 : (BASE_WEIGHTS[to][next] ?? 0.2);
    return w;
  });
}

function wantsSeventh(opts: GeneratorOptions, degree: Degree): boolean {
  const complexity = opts.style === "jazz" ? Math.max(2, opts.complexity) : opts.complexity;
  if (complexity === 1) return false;
  if (complexity === 3) return true;
  return degree === 2 || degree === 5;
}

/** Explain a move between two chords using only their actual notes and functions. */
export function explainMove(a: DiatonicChord, b: DiatonicChord): string {
  const up = ((b.rootPc - a.rootPc) % 12 + 12) % 12;
  const motion =
    up === 5 ? "the root falls a fifth (the strongest root motion)" :
    up === 7 ? "the root rises a fifth" :
    up === 0 ? "the root stays" :
    [3, 4, 8, 9].includes(up) ? `the root moves by a ${intervalName(Math.min(up, 12 - up))}` :
    "the root moves by step";
  const common = a.pitchClasses.filter((pc) => b.pitchClasses.includes(pc)).length;
  const shared = common === 0 ? "no shared notes" : `${common} shared note${common > 1 ? "s" : ""}`;
  let fn = "";
  if (a.function === "dominant" && b.function === "tonic") fn = "; dominant resolves to tonic";
  else if (a.function === "predominant" && b.function === "dominant") fn = "; predominant leads to dominant";
  else if (a.function === "tonic" && b.function !== "tonic") fn = `; moves away from home to a ${b.function} chord`;
  return `${a.roman} → ${b.roman}: ${motion}, ${shared}${fn}.`;
}

export function describeCadence(chords: readonly { degree: Degree }[]): string | null {
  const n = chords.length;
  const last = chords[n - 1]?.degree, prev = chords[n - 2]?.degree;
  if (prev === 5 && last === 1) return "Ends with an authentic cadence (V → I): the strongest sense of arrival.";
  if (prev === 4 && last === 1) return "Ends with a plagal cadence (IV → I), often heard as an “Amen” ending.";
  if (last === 5) return "Ends on V (a half cadence): it feels open and wants to continue.";
  if (last === 1) return "Ends on the tonic chord.";
  return null;
}

/** Explain an (edited or generated) list of degrees in a key, with the same grounded rules. */
export function describeProgression(key: MusicalKey, items: readonly { degree: Degree; seventh: boolean }[]): GeneratedChord[] {
  return items.map((it, i) => {
    const chord = diatonicChord(key, it.degree, it.seventh);
    const prev = i > 0 ? diatonicChord(key, items[i - 1]!.degree, items[i - 1]!.seventh) : null;
    return { degree: it.degree, seventh: it.seventh, chord, reason: prev ? explainMove(prev, chord) : `${chord.roman} (${chord.symbol}) opens the progression.` };
  });
}

export function generateProgression(input: GeneratorOptions): GeneratedProgression {
  const opts = { ...input, length: Math.max(2, Math.min(8, Math.round(input.length))) };
  const rng: Rng = seededRng(opts.seed);
  const fixed = new Map<number, Degree>();
  const ending = CADENCE_DEGREES[opts.cadence];
  ending.forEach((d, i) => fixed.set(opts.length - ending.length + i, d));
  // The cadence wins if the progression is too short for both constraints.
  if (opts.startOnTonic && opts.length > ending.length) fixed.set(0, 1);

  const degrees: Degree[] = [];
  for (let i = 0; i < opts.length; i++) {
    const f = fixed.get(i);
    if (f !== undefined) { degrees.push(f); continue; }
    const prev = degrees[i - 1];
    const next = fixed.get(i + 1);
    if (prev === undefined) {
      // Free first chord: weight by mood only (tonic most likely).
      const w = DEGREES.map((d) => (d === 1 ? 4 : 1) * MOOD_BIAS[opts.mood][FAMILY[diatonicChord(opts.key, d).quality] ?? "major"] * (next !== undefined ? (d === next ? 0 : (BASE_WEIGHTS[d][next] ?? 0.2)) : 1));
      degrees.push(DEGREES[weightedPick(w, rng)]!);
      continue;
    }
    degrees.push(DEGREES[weightedPick(weightsFrom(opts, prev, next), rng)]!);
  }

  const chords = describeProgression(opts.key, degrees.map((degree) => ({ degree, seventh: wantsSeventh(opts, degree) })));
  return { chords, cadenceText: describeCadence(chords), seed: opts.seed };
}
